import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Ornament } from "../components/Ornament";
import { storytellerDisplayName } from "../lib/storyteller";

// A short curated list. If the user's own timezone is missing we push it in.
const COMMON_TZS = [
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Rome", "Europe/Amsterdam", "Europe/Stockholm", "Europe/Warsaw", "Europe/Athens",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Vancouver", "America/Mexico_City", "America/Sao_Paulo",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo",
  "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
  "UTC",
];

export default function DeliverySettings() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh } = useAuth();
  const [time, setTime] = useState("08:00");
  const [tz, setTz] = useState("UTC");
  const [paused, setPaused] = useState(false);
  const [cadence, setCadence] = useState("daily");
  const [weekday, setWeekday] = useState(6); // Sunday
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=%2Fsettings%2Fdelivery", { replace: true }); return; }
    setTime(user.delivery_time || "08:00");
    setTz(user.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone) || "UTC");
    setPaused(!!user.delivery_paused);
    setCadence(user.delivery_cadence || "daily");
    setWeekday(typeof user.delivery_weekday === "number" ? user.delivery_weekday : 6);
  }, [user, authLoading, navigate]);

  const tzOptions = React.useMemo(() => {
    const list = [...COMMON_TZS];
    if (tz && !list.includes(tz)) list.unshift(tz);
    return list;
  }, [tz]);

  const onSave = async () => {
    setError(""); setSaving(true);
    try {
      await api.patch("/me/delivery", {
        delivery_time: time,
        timezone: tz,
        delivery_paused: paused,
        delivery_cadence: cadence,
        delivery_weekday: weekday,
      });
      if (refresh) await refresh();
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save just now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen paper" data-testid="delivery-settings-page">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8">
        <Link to="/my-living-book" className="inline-flex items-center gap-2 text-[#1B2A4A] hover:text-[#B8973A] transition-colors" style={{ minHeight: 44, fontFamily: "Georgia, serif", fontSize: "15px" }} data-testid="ds-back">
          <ChevronLeft size={18} /> Back to My Living Book
        </Link>
      </header>

      <main className="max-w-xl mx-auto px-6 sm:px-10 pb-16">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.35em]">DELIVERY SETTINGS</p>
        <h1 className="mt-4 text-3xl sm:text-4xl" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, color: "#111D33", lineHeight: 1.15 }} data-testid="ds-heading">
          {(() => {
            const n = storytellerDisplayName(user);
            return n ? <>When would you like {n}&rsquo;s next question?</> : <>When would you like tomorrow&rsquo;s question?</>;
          })()}
        </h1>
        <Ornament className="my-8" />

        <div className="space-y-8">
          <div>
            <label className="block mb-2 label-ui text-[11px] text-[#5C5548] tracking-[0.25em]">DELIVERY CHANNEL</label>
            <div className="space-y-3" data-testid="ds-channels">
              <div className="flex items-start gap-3 hairline-cream bg-white p-4" style={{ borderRadius: 4 }} data-testid="ds-channel-email">
                <div className="mt-1" aria-hidden="true">
                  <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 4, background: "#B8973A" }} />
                </div>
                <div>
                  <p className="text-[#111D33]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", fontWeight: 600 }}>Email</p>
                  <p className="text-[#5C5548]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }}>Your daily question is delivered to your inbox.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 hairline-cream p-4" style={{ borderRadius: 4, background: "#F3EEE3", opacity: 0.7 }} data-testid="ds-channel-whatsapp-soon" aria-disabled="true">
                <div className="mt-1" aria-hidden="true">
                  <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 4, background: "#C7B98A" }} />
                </div>
                <div>
                  <p className="text-[#111D33]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", fontWeight: 600 }}>WhatsApp <span className="ml-2 label-ui text-[10px] text-[#B8973A] tracking-[0.25em]">COMING SOON</span></p>
                  <p className="text-[#5C5548]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }}>Receive your daily question as a WhatsApp message. Available soon.</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 label-ui text-[11px] text-[#5C5548] tracking-[0.25em]">HOW OFTEN?</label>
            <div className="grid grid-cols-2 gap-2" data-testid="ds-cadence">
              <button type="button" onClick={() => setCadence("weekly")}
                      className={`px-4 py-4 hairline-cream transition ${cadence === "weekly" ? "bg-[#B8973A] text-[#FAF7F2]" : "bg-white text-[#1B2A4A] hover:bg-[#F7F1E5]"}`}
                      style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "15px", letterSpacing: "0.1em", minHeight: 52 }}
                      data-testid="ds-cadence-weekly">
                WEEKLY
              </button>
              <button type="button" onClick={() => setCadence("daily")}
                      className={`px-4 py-4 hairline-cream transition ${cadence === "daily" ? "bg-[#B8973A] text-[#FAF7F2]" : "bg-white text-[#1B2A4A] hover:bg-[#F7F1E5]"}`}
                      style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "15px", letterSpacing: "0.1em", minHeight: 52 }}
                      data-testid="ds-cadence-daily">
                DAILY
              </button>
            </div>
            <p className="mt-2 text-[#8C8278]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }}>
              {cadence === "weekly"
                ? "One question a week, so you can sit down together properly."
                : "One question a day, like the classic Story Week."}
            </p>
          </div>

          {cadence === "weekly" && (
            <div data-testid="ds-weekday-block">
              <label htmlFor="ds-weekday" className="block mb-2 label-ui text-[11px] text-[#5C5548] tracking-[0.25em]">WHICH DAY OF THE WEEK?</label>
              <select id="ds-weekday" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}
                      className="w-full px-5 py-4 hairline-cream bg-white text-[#1B2A4A]"
                      style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "17px", minHeight: 48 }}
                      data-testid="ds-weekday-select">
                {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((label, i) => (
                  <option key={i} value={i}>{label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="ds-time" className="block mb-2 label-ui text-[11px] text-[#5C5548] tracking-[0.25em]">{cadence === "weekly" ? "DELIVERY TIME" : "DAILY DELIVERY TIME"}</label>
            <input
              id="ds-time" type="time" value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full px-5 py-4 hairline-cream bg-white text-[#1B2A4A]"
              style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "17px", minHeight: 48 }}
              data-testid="ds-time-input"
            />
            <p className="mt-2 text-[#8C8278]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }}>
              Tomorrow&rsquo;s email arrives at this time in your timezone.
            </p>
          </div>

          <div>
            <label htmlFor="ds-tz" className="block mb-2 label-ui text-[11px] text-[#5C5548] tracking-[0.25em]">TIMEZONE</label>
            <select
              id="ds-tz" value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="w-full px-5 py-4 hairline-cream bg-white text-[#1B2A4A]"
              style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "17px", minHeight: 48 }}
              data-testid="ds-tz-select"
            >
              {tzOptions.map((z) => (<option key={z} value={z}>{z}</option>))}
            </select>
          </div>

          <label className="flex items-center gap-3 cursor-pointer" data-testid="ds-pause-label">
            <input
              type="checkbox" checked={paused}
              onChange={(e) => setPaused(e.target.checked)}
              className="w-5 h-5"
              data-testid="ds-pause-input"
            />
            <span className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "16px" }}>
              Pause Story Week emails. My question waits until I&rsquo;m ready.
            </span>
          </label>

          {error && <p className="text-red-700" role="alert" data-testid="ds-error">{error}</p>}

          <div>
            <button
              onClick={onSave} disabled={saving}
              className="btn-gold w-full sm:w-auto"
              style={{ minHeight: 52, padding: "14px 32px", fontSize: "13px", letterSpacing: "0.2em" }}
              data-testid="ds-save-btn"
            >
              {saving ? "Saving…" : "SAVE DELIVERY SETTINGS"}
            </button>
            {savedAt && (
              <p className="mt-3 label-ui text-[11px] text-[#B8973A] tracking-[0.25em]" data-testid="ds-saved">
                SAVED
              </p>
            )}
          </div>

          {user?.is_admin && <AdminTestEmails email={user.email} />}
        </div>
      </main>
    </div>
  );
}

function AdminTestEmails({ email }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const onFire = async () => {
    setErr(""); setResult(null); setSending(true);
    try {
      const { data } = await api.post("/qa/send-test-emails");
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-16 pt-8 border-t" style={{ borderColor: "#E8E1D3" }} data-testid="ds-admin-block">
      <p className="label-ui text-[10px] text-[#8C8278] tracking-[0.35em]">ADMIN · SEND TEST EMAILS</p>
      <p className="mt-3 text-[#5C5548]" style={{ fontFamily: "Georgia, serif", fontSize: "14px", lineHeight: 1.65 }}>
        Sends Day 1, Day 2 and a Day 1 reminder to <strong>{email}</strong>. Magic links open your own account.
        Logged as <em>test: true</em>. Limited to 5 runs per hour.
      </p>
      <button
        onClick={onFire} disabled={sending}
        className="mt-4 hairline-cream bg-white text-[#1B2A4A] px-5 py-3 hover:bg-[#F7F1E5] transition"
        style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "13px", letterSpacing: "0.15em" }}
        data-testid="ds-admin-send-btn"
      >
        {sending ? "SENDING…" : "SEND 3 TEST EMAILS"}
      </button>
      {result && (
        <div className="mt-4 text-[#5C5548]" style={{ fontFamily: "Georgia, serif", fontSize: "14px", lineHeight: 1.7 }} data-testid="ds-admin-result">
          {result.ok ? (
            <p>Test emails sent to <strong>{result.sent_to}</strong> from <em>{result.sender}</em>. Links point to <code style={{ fontSize: 12 }}>{result.origin}</code>.</p>
          ) : (
            <p className="text-red-700">Some emails failed:</p>
          )}
          <ul className="mt-2 text-[12px]" style={{ color: "#8C8278" }}>
            {(result.results || []).map((r, i) => (
              <li key={i}>Day {r.day} {r.kind}: {r.status}{r.provider_id ? ` · id ${r.provider_id.slice(0, 12)}…` : ""}{r.error ? ` · ${r.error}` : ""}</li>
            ))}
          </ul>
        </div>
      )}
      {err && <p className="mt-3 text-red-700" role="alert" data-testid="ds-admin-error">{err}</p>}
    </div>
  );
}
